import { Autocomplete, Card, CardContent, Dialog, DialogContent, Grid, Icon, IconButton, TextField } from '@mui/material'
import { DELETE, SAVE } from 'common/components/action/ActionConstant'
import { CardTable } from 'common/components/datatable/Table'
import Input from 'common/components/form/Input'
import useFetch from 'common/hooks/useFetch'
import ApplicationConf from 'conf/ApplicationConf'
import RadioButtons from 'common/components/form/RadioButtons'
import Select from 'common/components/form/Select'
import ConfirmModal from 'common/components/modal/ConfirmModal'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import { JsonEditor } from 'json-edit-react'
import { isNil } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useActions from 'common/hooks/useActions'
import useBoolean from 'common/hooks/useBoolean'
import useParseJson from 'common/hooks/useParseJson'
import useTitles from 'common/hooks/useTitles'
import useUpdateEffect from 'common/hooks/useUpdateEffect'
import { isJson } from 'common/utils/StringUtil'
import PropTypes from 'prop-types'
import { hasPermission } from 'common/utils/HabilitationUtil'
import { useNavigate, useOutletContext, useParams } from 'react-router'
import { deleteInstance, updateInstance } from 'supervision/instances/InstancesSlice'
import { fetchCustomers, selectCustomers } from 'gdl/customers/CustomersSlice'
import { INSTANCE_TYPE } from '../constants/InstanceConstants'

const DISPLAY_MODE = {
    TEXT: 'TEXT',
    JSON: 'JSON',
}

const InstanceParameterDialog = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    parameter,
}) => {
    const [displayMode, setDisplayMode] = useState(DISPLAY_MODE.TEXT)

    const [key, setKey] = useState('')
    const [value, setValue] = useState('')

    // Noms de paramètres existants (toutes instances) pour l'autocomplete ; un nom inédit reste saisissable.
    const { data: paramOptions = [] } = useFetch(isOpen ? ApplicationConf.instance.parameters() : undefined)

    const {
        json: jsonData,
        error,
    } = useParseJson(value ?? '{}')

    useUpdateEffect(() => {
        if (!isOpen) return

        setKey(parameter?.parameter ?? '')
        const paramValue = parameter?.value ?? ''
        setValue(paramValue)
        setDisplayMode(isJson(paramValue) ? DISPLAY_MODE.JSON : DISPLAY_MODE.TEXT)
    }, [isOpen])

    const updateJsonObj = (newJson) => {
        setValue(JSON.stringify(newJson))
    }

    return (
        <Dialog
            open={isOpen}
            maxWidth={'lg'}
        >
            <BasicDialogTitle onClose={onClose}>
                Paramètre
            </BasicDialogTitle>
            <DialogContent style={{ overflowX: 'hidden' }}>
                <Card>
                    <CardContent>
                        <Grid container columnSpacing={2} rowSpacing={1} alignItems='center' style={{ paddingTop: '5px' }}>
                            <Grid size={6}>
                                <Autocomplete
                                    freeSolo
                                    options={paramOptions}
                                    value={key ?? ''}
                                    onChange={(_, v) => setKey(v ?? '')}
                                    inputValue={key ?? ''}
                                    onInputChange={(_, v) => setKey(v)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label='Paramètre'
                                            InputProps={{ ...params.InputProps, notched: false }}
                                            InputLabelProps={{ ...params.InputLabelProps, shrink: false }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={6}>
                                <RadioButtons
                                    label={'Mode d\'affichage'}
                                    items={[{ value: DISPLAY_MODE.TEXT, label: 'Text' }, { value: DISPLAY_MODE.JSON, label: 'Json' }]}
                                    value={displayMode}
                                    onChange={(newMode) => {
                                        if (newMode === DISPLAY_MODE.JSON && !value) {
                                            setValue('{}')
                                        }
                                        setDisplayMode(newMode)
                                    }}
                                />
                            </Grid>
                            <Grid size={12}>
                                {displayMode === DISPLAY_MODE.TEXT && (
                                    <Input
                                        label='Valeur'
                                        value={value}
                                        onChange={setValue}
                                        multiline
                                    />
                                )}
                                {displayMode === DISPLAY_MODE.JSON && (
                                    <>
                                        {!!error && (
                                            <h6 style={{ padding: '0 5 10 5', fontSize: '16px', margin: 0 }}>
                                                Erreur: {error}
                                            </h6>
                                        )}
                                        <JsonEditor
                                            data={jsonData}
                                            setData={updateJsonObj}
                                            maxWidth='100%'
                                            rootFontSize='12px'
                                            rootName='Valeur'
                                            enableClipboard={false}
                                        />
                                    </>
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <BasicDialogAction
                buttonLabel={isNil(parameter) ? 'Créer' : 'Modifier'}
                onValidate={() => onValidate({ parameter: key, value })}
                isDisabled={!key || !value}
            />
        </Dialog>
    )
}

InstanceParameterDialog.propTypes = {
    isOpen: PropTypes.bool,
    parameter: PropTypes.shape({
        parameter: PropTypes.string,
        value: PropTypes.string,
    }),
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
}

const InstanceDescriptionApp = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { user } = useSelector(store => ({ user: store.app.user }), shallowEqual)
    const { instance, reloadInstance } = useOutletContext()

    // Client associé à l'instance : celui dont la liste d'instances contient cette instance.
    const customers = useSelector(selectCustomers, shallowEqual)
    useEffect(() => {
        dispatch(fetchCustomers())
    }, [dispatch])
    const associatedCustomer = useMemo(
        () => customers.find(c => c.instances?.some(ci => ci.instance === instance?.id)),
        [customers, instance],
    )

    useTitles(() => [{
        label: 'Instance',
        href: '/instance',
    }, {
        label: isNil(instance) ? 'Chargement...' : (instance.name ?? 'Inconnue'),
        href: `/instance/${id}`,
    }, {
        label: 'Description',
        href: `/instance/${id}/description`,
    }], [instance, id])

    const {
        value: isPopinOpen,
        setTrue: onOpenPopin,
        setFalse: onClosePopin,
    } = useBoolean(false)
    const {
        value: isConfirmOpen,
        setTrue: onOpenConfirm,
        setFalse: onCloseConfirm,
    } = useBoolean(false)
    const [selectedParameter, setSelectedParameter] = useState()

    const [name, setName] = useState(instance?.name)
    const [server, setServer] = useState(instance?.server)
    const [instanceType, setInstanceType] = useState(instance?.instanceType ?? undefined)
    const [url, setUrl] = useState(instance?.url)
    const [parameters, setParameters] = useState(instance?.parameters ?? [])

    useActions(() => {
        if (isNil(instance)) return []

        const shouldPulse = name !== instance.name
                         || server !== instance.server
                         || instanceType !== instance.instanceType
                         || url !== instance.url
                         || parameters.length !== instance.parameters.length
                         || parameters.some(param => !instance.parameters.some(p => p.parameter === param.parameter && p.value === param.value))

        return [
            {
                type: SAVE,
                onClick: () => dispatch(updateInstance({ id: instance.id, name, server, instanceType, url, parameters })).then(res => {
                    if (res.payload?.update > 0) {
                        reloadInstance()
                    }
                }),
                pulse: shouldPulse,
                displayed: hasPermission(user, 'instances', 'update'),
            },
            {
                type: DELETE,
                onClick: () => dispatch(deleteInstance(instance.id)).then(res => {
                    if (res.payload?.delete > 0) {
                        navigate('/instance')
                    }
                }),
                displayed: hasPermission(user, 'instances', 'delete'),
            },
        ]
    }, [instance, name, server, instanceType, url, parameters])

    useUpdateEffect(() => {
        setName(instance?.name)
        setServer(instance?.server)
        setInstanceType(instance?.instanceType ?? undefined)
        setUrl(instance?.url)
        setParameters(instance?.parameters ?? [])
    }, [instance])

    const onDeleteParameter = row => {
        setSelectedParameter(row.parameter)
        onOpenConfirm()
    }

    const onEditParameter = row => {
        setSelectedParameter(row.parameter)
        onOpenPopin()
    }

    const parameter = parameters.find(p => p.parameter === selectedParameter)
    const canUpdate = hasPermission(user, 'instances', 'update')

    return (
        <Grid container spacing={1}>
            <Grid size={12}>
                <Card>
                    <CardContent>
                        <Grid container columnSpacing={2} rowSpacing={1} alignItems='center' style={{ paddingTop: '5px' }}>
                            <Grid size={6}>
                                <Input
                                    label={'Nom'}
                                    value={name}
                                    onChange={setName}
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    label={'Serveur'}
                                    value={server}
                                    onChange={setServer}
                                    type='float'
                                />
                            </Grid>
                            <Grid size={6}>
                                <Select
                                    label={'Type d\'instance'}
                                    items={INSTANCE_TYPE}
                                    value={instanceType}
                                    onChange={setInstanceType}
                                    undefinedValue
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    label={'Url'}
                                    value={url}
                                    onChange={setUrl}
                                />
                            </Grid>
                            {associatedCustomer && (
                                <Grid size={6}>
                                    <Grid container alignItems='center' columnSpacing={0.5} wrap='nowrap'>
                                        <Grid size='grow'>
                                            <Select
                                                label='Client associé'
                                                value={associatedCustomer.id}
                                                items={customers.map(c => ({ value: c.id, label: c.mnemonic || c.name }))}
                                                onChange={() => {}}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid size='auto'>
                                            <IconButton onClick={() => navigate(`/customer/${associatedCustomer.id}`)} title='Accéder au client'>
                                                <Icon>open_in_new</Icon>
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={12}>
                <CardTable
                    title='Paramètrage'

                    rows={parameters}
                    headers={[{ key: 'parameter', value: 'Paramètre' }, { key: 'value', value: 'Valeur' }]}

                    actions={[{
                        icon: 'add',
                        onClick: onOpenPopin,
                        tooltip: 'Nouveau paramètre',
                        displayed: canUpdate,
                    }]}
                    lineActions={[
                        { icon: 'delete', onClick: onDeleteParameter, displayed: canUpdate },
                        { icon: 'edit', onClick: onEditParameter, displayed: canUpdate },
                    ]}
                />
                <InstanceParameterDialog
                    isOpen={isPopinOpen}
                    parameter={parameter}
                    onClose={() => {
                        setSelectedParameter()
                        onClosePopin()
                    }}
                    onValidate={newParam => {
                        setParameters(params => {
                            const param = { ...newParam, instanceId: instance?.id }
                            if (isNil(selectedParameter)) return [...params, param]
                            return params.map(p => p.parameter === selectedParameter ? param : p)
                        })
                        setSelectedParameter()
                        onClosePopin()
                    }}
                />
                <ConfirmModal
                    isOpen={isConfirmOpen}
                    title='Êtes-vous sûr de vouloir supprimer ce paramètrage ?'
                    onValidate={() => {
                        setParameters(params => params.filter(p => p.parameter !== selectedParameter))
                        onCloseConfirm()
                    }}
                    onClose={onCloseConfirm}
                />
            </Grid>
        </Grid>
    )
}

export default InstanceDescriptionApp
export {
    InstanceParameterDialog,
}